import type { JSX } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { Button, Notice, TextField } from '../components/ui.js';

/**
 * Установка нового пароля по ссылке из письма.
 *
 * Открывается без входа — человек как раз не может войти, в этом всё и дело.
 * Токен приходит параметром адреса; после успешной смены сервер сразу выдаёт
 * сессию, поэтому входить повторно не приходится.
 */

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = repeat.length > 0 && password !== repeat;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (mismatch || password.length < 8) return;

    setBusy(true);
    setError(null);
    try {
      await api.auth.resetPassword(token, password);
      // Сессия уже выдана сервером — просто перечитываем, кто мы теперь.
      await refresh();
      window.location.href = '/';
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не получилось. Попробуйте ещё раз.');
      setBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <main className="app-main" style={{ display: 'grid', alignContent: 'center' }}>
        <section className="panel stack" style={{ maxWidth: 420, width: '100%', margin: '0 auto' }}>
          <h1 style={{ fontSize: '1.4rem' }}>Новый пароль</h1>

          {!token ? (
            <Notice tone="danger">
              В ссылке нет кода восстановления. Откройте её из письма целиком —
              возможно, она разорвалась при пересылке.
            </Notice>
          ) : (
            <form className="stack" onSubmit={submit}>
              <TextField
                label="Придумайте пароль"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                hint="Минимум 8 символов. После смены все другие устройства выйдут из аккаунта."
                required
                autoFocus
              />

              <TextField
                label="Ещё раз"
                type="password"
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
                autoComplete="new-password"
                error={mismatch ? 'Пароли не совпадают' : null}
                required
              />

              {error && (
                <Notice tone="danger">
                  {error}
                </Notice>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={busy}
                disabled={password.length < 8 || mismatch}
                block
              >
                Сохранить и войти
              </Button>
            </form>
          )}

          <a href="/" className="small center text-link">
            Вернуться ко входу
          </a>
        </section>
      </main>
    </div>
  );
}
